import { InvalidInputError } from "./errors/inputError";

export interface ProjectI {
    name: string;
    count?: number;
    completed: boolean;
    creationDate: string;
    description: string;
    regeneration: boolean;
    isPublic: boolean;
    threshold: number | null;
    groups?: string[];
}

export class Project {
    name: string;
    count: number;
    completed: boolean;
    creationDate: Date;
    description: string;
    regeneration: boolean;
    isPublic: boolean;
    threshold?: number;
    groups: Set<string>;

    constructor(name: string, count: number, completed: boolean, description: string, regeneration: boolean, isPublic: boolean, threshold?: number, creationDate?: string | Date, groups?: string[]) {
        this.name = name;
        this.count = count;
        this.completed = completed;
        if (creationDate instanceof Date) {
            this.creationDate = creationDate;
        } else if (creationDate) {
            this.creationDate = new Date(creationDate);
        } else {
            this.creationDate = new Date();
        }
        this.description = description;
        this.regeneration = regeneration;
        this.isPublic = isPublic;
        this.threshold = threshold;
        this.groups = new Set(groups ?? []);
    }

    public static fromObject(obj: ProjectI) {
        return new Project(
            obj.name,
            obj.count ?? 0,
            obj.completed,
            obj.description,
            obj.regeneration,
            obj.isPublic ?? false,
            obj.threshold ?? undefined,
            obj.creationDate,
            obj.groups ?? []
        );
    }

    /**
     * Verifies project object is actually a project.
     */
    public static ensureInstance(obj: Project | ProjectI | (Partial<ProjectI> & { creationDate?: string | Date; groups?: string[] | Set<string> })): Project {
        if (obj instanceof Project) {
            return obj;
        }

        const rawGroups = obj.groups;
        const groups = rawGroups instanceof Set ? Array.from(rawGroups) : (rawGroups ?? []);

        return new Project(
            obj.name ?? "",
            obj.count ?? 0,
            obj.completed ?? false,
            obj.description ?? "",
            obj.regeneration ?? false,
            obj.isPublic ?? false,
            obj.threshold ?? undefined,
            obj.creationDate,
            groups
        );
    }

    /**
     * Serializes the project into the interface format.
     */
    public toJson(): ProjectI {
        return {
            name: this.name,
            count: this.count,
            completed: this.completed,
            creationDate: this.creationDate.toISOString(),
            description: this.description,
            regeneration: this.regeneration,
            isPublic: this.isPublic,
            threshold: this.threshold ?? null,
            groups: Array.from(this.groups)
        };
    }

    public toggleRegen(): void {
        this.regeneration = !this.regeneration;
    }

    public copy(): Project {
        return new Project(
            this.name,
            this.count,
            this.completed,
            this.description,
            this.regeneration,
            this.isPublic,
            this.threshold,
            this.creationDate,
            Array.from(this.groups)
        );
    }

    public get Threshold(): number | undefined { return this.threshold; }
    public set Threshold(value: number | undefined) {
        if (value !== undefined && value < 1) {
            throw new InvalidInputError("Project threshold cannot be less than 1");
        }
        this.threshold = value;
    }
}